/**
 * nodeSync.ts — position resolution for the canvas structure sync.
 *
 * The derived node memo substitutes `{x:0,y:0}` for nodes without an authored
 * position (auto-layout), so syncing into React Flow's store can't blindly
 * take the derived position — auto-laid-out nodes would snap to the origin on
 * every unrelated edit. It also can't blindly keep RF's live position — then
 * undoing a node/group drag would restore the bundle while the canvas keeps
 * showing the dragged position (undo looks broken).
 *
 * The rule: a node WITH an authored position always renders at it (the sync
 * compares the authored position against RF's current one and lands the
 * authored value whenever they differ — which is exactly "a drag commit, an
 * undo or a redo just arrived"). Nodes WITHOUT an authored position keep
 * RF's live position — the layout work RF did is the only source of truth
 * there. Mid-drag syncs are gated off by `draggingRef` in the canvas.
 */

import type { Graph, NodeGroup } from '../engine/types';

export interface SyncPoint {
  x: number;
  y: number;
}

/** Every node/group's authored position for one graph revision. Actor nodes
 *  map to their optional `position`; group rects map to their origin (rect
 *  moves ARE authored edits — undoing them must move the rect back). */
export function authoredPositions(
  graph: Graph,
  groups?: NodeGroup[],
): Map<string, SyncPoint | undefined> {
  const m = new Map<string, SyncPoint | undefined>();
  for (const n of graph.nodes) m.set(n.id, n.position);
  for (const g of groups ?? []) m.set(g.id, { x: g.rect.x, y: g.rect.y });
  return m;
}

/** Resolve one node's position during a structure sync.
 *  - no authored position → RF's live position (auto-layout survives), with
 *    the derived `fresh` value as the fallback for nodes RF hasn't seen yet;
 *  - authored position equal to RF's current one → RF's (no churn);
 *  - authored position differs → the authored one, so drag commits, undo and
 *    redo all land on the canvas. */
export function syncPositionFor(
  id: string,
  fresh: SyncPoint,
  existing: SyncPoint | undefined,
  authored: Map<string, SyncPoint | undefined>,
): SyncPoint {
  const a = authored.get(id);
  if (!a) return existing ?? fresh;
  if (existing && existing.x === a.x && existing.y === a.y) return existing;
  return a;
}
